import React, { useState, useEffect } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Box, Typography, Paper, Button, CircularProgress } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { canAdministerObservatory, hasLegacyAdminRole } from '../../utils/userCapabilities';

const ObservatoryAdminRoute = () => {
  const { user } = useAuth();
  const { id: observatoryId } = useParams();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        setLoading(true);
        
        // Server admins have access to everything
        if (hasLegacyAdminRole(user)) {
          setHasAccess(true);
          setLoading(false);
          return;
        }

        // For non-server admins, check observatory-specific permissions
        if (user?.id || user?._id) {
          const userId = user.id || user._id;
          const response = await apiService.getUserMemberships(userId);
          
          if (response?.success) {
            const userMemberships = response.data || [];

            // Check if user can administer this specific observatory
            const canAdmin = canAdministerObservatory(userMemberships, observatoryId);
            setHasAccess(canAdmin);
          }
        }
      } catch (error) {
        console.error('Error checking observatory access:', error);
        setHasAccess(false);
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [user, observatoryId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasAccess) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            maxWidth: 400,
          }}
        >
          <LockIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
          <Typography variant="h5" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            You don't have administrative privileges for this observatory. 
            Contact the observatory owner to request access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.history.back()}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Paper>
      </Box>
    );
  }

  return <Outlet />;
};

export default ObservatoryAdminRoute;